/**
 * @fileoverview Ops console — the Founding Ensemble cohort (Phase One).
 *
 * Two panels: mint invitations, and read what the cohort has sent back.
 *
 * **Minting lives here and nowhere else** because a founding invitation is a
 * complimentary year of the top tier, and the only identity that should be able
 * to hand one out is the one that already controls platform-wide configuration.
 * No instructor-facing surface can issue one.
 *
 * **The console produces a link; it does not send an email.** The whole premise
 * is a personal invitation from someone the teacher knows — a templated message
 * from a robot is the one thing that would make an exclusive cohort feel like a
 * mailing list. Copy the URL, write the email yourself.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import opsApi, { getOpsErrorMessage } from "@/lib/opsApi";
import { Button, Card, Icon, SectionHeader, Tag } from "@/components/studio/primitives";

const field =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function OpsFoundingPage() {
  const [invites, setInvites] = useState([]);
  const [cohortOpen, setCohortOpen] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [foundingOnly, setFoundingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [minting, setMinting] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", note: "" });
  const [sendEmail, setSendEmail] = useState(true);
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    try {
      // allSettled, not all, for two reasons. It keeps whichever half DID load
      // — an invite list is still useful when the feedback read fails — and
      // Promise.all leaves the loser's rejection unhandled when both reject,
      // which is a real unhandled-rejection in the browser and not just noise.
      const [invitesRes, feedbackRes] = await Promise.allSettled([
        opsApi.get("/ops/founding/invites"),
        opsApi.get("/ops/feedback", { params: { founding_only: foundingOnly } }),
      ]);

      if (invitesRes.status === "fulfilled") {
        setInvites(invitesRes.value.data.invites || []);
        setCohortOpen(invitesRes.value.data.cohort_open);
      }
      if (feedbackRes.status === "fulfilled") {
        setFeedback(feedbackRes.value.data.feedback || []);
      }

      const failed = [invitesRes, feedbackRes].find((r) => r.status === "rejected");
      if (failed) throw failed.reason;
      setLoadError(false);
    } catch (err) {
      // Recorded, not just toasted. A toast vanishes and leaves the empty
      // states below asserting "No invitations yet" — which the page does not
      // know and which reads as data rather than as a failed read. This
      // happened for real: the frontend deploys ~4x faster than the backend, so
      // for several minutes after a release the page exists and its endpoints
      // do not.
      setLoadError(true);
      toast.error(getOpsErrorMessage(err, "Couldn't load the cohort"));
    } finally {
      setLoading(false);
    }
  }, [foundingOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const mint = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || minting) return;
    setMinting(true);
    try {
      const to = form.email.trim();
      const res = await opsApi.post("/ops/founding/invites", {
        email: to,
        name: form.name.trim() || null,
        note: form.note.trim() || null,
        send_email: sendEmail,
      });
      const url = res.data.invite.url;
      // The link is copied whatever happened to the email. The send is
      // best-effort on the server, so this is the path back when it fails —
      // and the failure is surfaced as a failure rather than folded into a
      // success toast, because an admin who thinks a teacher was emailed will
      // not follow up.
      await navigator.clipboard.writeText(url).catch(() => {});
      // An already-redeemed address is not a failed send, and saying so would
      // blame the mail server for the one thing the admin actually wants to
      // know: this teacher is already in. The server refuses to email it for
      // the same reason.
      if (res.data.invite.status === "accepted") toast.success(`${to} has already joined the cohort`);
      else if (sendEmail && res.data.email_sent) toast.success(`Invitation emailed to ${to} — link copied too`);
      else if (sendEmail) toast.error("Invitation created, but the email didn't send. The link is on your clipboard.");
      else toast.success("Invitation ready — link copied");
      setForm({ email: "", name: "", note: "" });
      load();
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Couldn't create that invitation"));
    } finally {
      setMinting(false);
    }
  };

  const resend = async (invite) => {
    setResending(invite.token);
    try {
      await opsApi.post(`/ops/founding/invites/${invite.token}/resend`);
      toast.success(`Invitation re-sent to ${invite.email}`);
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Couldn't send that email"));
    } finally {
      setResending(null);
    }
  };

  const revoke = async (token) => {
    try {
      await opsApi.delete(`/ops/founding/invites/${token}`);
      toast.success("Invitation revoked");
      load();
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Couldn't revoke that invitation"));
    }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
        <p className="font-sans text-sm text-[var(--text-tertiary)]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8 flex flex-col gap-8">
      {loadError && (
        <div
          data-testid="founding-load-error"
          className="rounded-[14px] border border-[var(--state-danger-border)] bg-[var(--state-danger-dim)] p-4"
        >
          <p className="font-sans text-sm text-[var(--text-primary)]">Couldn't load the cohort.</p>
          <p className="mt-1 font-sans text-sm text-[var(--text-tertiary)]">
            The lists below are not showing real data. If this is just after a release, the backend may still be
            deploying — it lands a few minutes behind the frontend.
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-3 rounded-full border border-[var(--studio-divider-strong)] px-4 py-1.5 font-sans text-sm text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]"
          >
            Try again
          </button>
        </div>
      )}

      <div>
        <SectionHeader>Founding Ensemble</SectionHeader>
        {cohortOpen === false && (
          // The flag is the way to cap a cohort that is meant to stay small, so
          // its state belongs on this page rather than only in the flags list.
          <p className="mt-2 font-sans text-sm text-[var(--state-warning,var(--accent-yellow))]">
            Enrolment is currently closed — <code className="font-mono">FOUNDING_COHORT_ENABLED</code> is off, so these
            links will not open. Members already enrolled are unaffected.
          </p>
        )}
      </div>

      <Card>
        <h2 className="font-sans text-base font-semibold text-[var(--text-primary)]">Invite a teacher</h2>
        <p className="mt-1.5 font-sans text-sm text-[var(--text-tertiary)]">
          Creates a private, single-use link bound to this address. The link is copied to your clipboard either way, so
          you can always write the message yourself.
        </p>
        <form onSubmit={mint} className="mt-4 flex flex-col gap-3">
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="teacher@example.com"
            data-testid="founding-invite-email"
            className={field}
          />
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Their name (greets them on the page)"
            className={field}
          />
          <input
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Private note — why this teacher (never shown to them)"
            className={field}
          />
          <label className="flex items-start gap-2.5 py-0.5">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              data-testid="founding-send-email"
              className="mt-0.5 h-4 w-4 accent-[var(--accent-yellow)]"
            />
            <span className="font-sans text-sm text-[var(--text-tertiary)]">
              Email them the invitation. Untick to mint the link silently and send it yourself.
            </span>
          </label>
          <Button type="submit" disabled={minting} data-testid="founding-mint">
            {minting ? (sendEmail ? "Sending…" : "Creating…") : sendEmail ? "Send invitation" : "Create invitation"}
          </Button>
        </form>
      </Card>

      <div>
        <SectionHeader>{`Invitations (${invites.length})`}</SectionHeader>
        <div className="mt-3 flex flex-col gap-2">
          {invites.length === 0 && !loadError && (
            <p className="font-sans text-sm text-[var(--text-tertiary)]">No invitations yet.</p>
          )}
          {invites.map((invite) => (
            <Card key={invite.token} className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-sans text-[15px] text-[var(--text-primary)]">{invite.email}</span>
                  <Tag accent={invite.status === "accepted"}>{invite.status}</Tag>
                  {invite.status === "pending" && !invite.live && <Tag>expired</Tag>}
                </div>
                <p className="mt-1 font-mono text-xs text-[var(--text-tertiary)]">
                  {invite.name ? `${invite.name} · ` : ""}sent {formatDate(invite.created_at)}
                  {invite.status === "accepted" ? ` · joined ${formatDate(invite.accepted_at)}` : ""}
                  {invite.note ? ` · ${invite.note}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => copy(invite.url)}
                  className="rounded-[10px] border border-[var(--studio-divider-strong)] px-3 py-1.5 font-sans text-sm text-[var(--text-secondary)] hover:bg-[var(--studio-raised)]"
                >
                  <Icon name="copy" size={14} className="mr-1.5 inline" />
                  Copy link
                </button>
                {invite.status === "pending" && invite.live && (
                  // Only on a LIVE invitation: re-sending a dead link would
                  // deliver perfectly and then fail the teacher, which is
                  // worse than not offering it. Expired ones are refreshed
                  // from the form above.
                  <button
                    type="button"
                    disabled={resending === invite.token}
                    onClick={() => resend(invite)}
                    data-testid={`founding-resend-${invite.token}`}
                    className="rounded-[10px] border border-[var(--studio-divider-strong)] px-3 py-1.5 font-sans text-sm text-[var(--text-secondary)] hover:bg-[var(--studio-raised)] disabled:opacity-50"
                  >
                    <Icon name="mail" size={14} className="mr-1.5 inline" />
                    {resending === invite.token ? "Sending…" : "Resend"}
                  </button>
                )}
                {invite.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => revoke(invite.token)}
                    className="rounded-[10px] border border-[var(--studio-divider-strong)] px-3 py-1.5 font-sans text-sm text-[var(--text-tertiary)] hover:bg-[var(--studio-raised)]"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        {/* Composed directly rather than with SectionHeader: that primitive is
            already a justify-between row, so nesting it inside another one
            gives two contradictory rows for one line of UI. Its `action` slot
            renders a button, which a filter checkbox is not. Same title
            styling, plus a real heading element. */}
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            {`Feedback (${feedback.length})`}
          </h2>
          <label className="flex shrink-0 items-center gap-2 font-sans text-sm text-[var(--text-tertiary)]">
            <input
              type="checkbox"
              checked={foundingOnly}
              onChange={(e) => setFoundingOnly(e.target.checked)}
              className="h-4 w-4 accent-[var(--accent-yellow)]"
            />
            Founding members only
          </label>
        </div>
        <div className="mt-3 flex flex-col gap-2">
          {feedback.length === 0 && !loadError && (
            <p className="font-sans text-sm text-[var(--text-tertiary)]">Nothing yet.</p>
          )}
          {feedback.map((row) => (
            <Card key={row.feedback_id}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-sans text-[15px] text-[var(--text-primary)]">{row.name || row.email}</span>
                <Tag>{row.category}</Tag>
                {row.founding_member && <Tag accent>founding</Tag>}
                <span className="font-mono text-xs text-[var(--text-tertiary)]">
                  {formatDate(row.created_at)} · {row.page || "—"} · {row.client || "—"}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-[var(--text-secondary)]">
                {row.message}
              </p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
